<template>
  <div class="employee-insights-layout bg-gray-50">
    <!-- Sidebar -->
    <SideBar height-class="h-full">
      <div
        v-if="isSummaryLoading"
        class="mb-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
      >
        <div class="flex items-center gap-3">
          <div class="relative shrink-0">
            <div class="w-16 h-16 rounded-full border-4 border-brand-blue border-t-transparent animate-spin" />
            <div class="absolute inset-0 flex items-center justify-center">
              <div class="w-8 h-8 rounded-full border-4 border-brand-green border-t-transparent animate-spin-reverse" />
            </div>
          </div>
          <div class="min-w-0">
            <div class="text-sm font-semibold text-gray-800">
              Loading Staff Insights
            </div>
            <div class="truncate text-xs text-gray-500">
              {{ loadingSummaryText }}
            </div>
          </div>
        </div>
        <div
          v-if="loadingProgress.totalWeeks"
          class="mt-3 h-2 w-full overflow-hidden rounded-full bg-gray-200"
          role="progressbar"
          :aria-valuenow="loadingProgressPercent"
          aria-valuemin="0"
          aria-valuemax="100"
        >
          <div
            class="h-full rounded-full bg-brand-blue transition-all duration-300"
            :style="{ width: `${loadingProgressPercent}%` }"
          />
        </div>
        <div
          v-if="loadingProgress.totalWeeks"
          class="mt-2 flex justify-between text-xs text-gray-500"
        >
          <span>{{ loadingProgress.completedWeeks }} / {{ loadingProgress.totalWeeks }} weeks</span>
          <span>{{ loadingProgressPercent }}%</span>
        </div>
      </div>
      <div
        v-if="showHeatmapLegend"
        class="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
      >
        <div class="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Heatmap Legend
        </div>
        <p class="mt-2 text-xs leading-snug text-gray-500">
          Colors show the largest share of logged time for each worked day. Target issues are highlighted separately.
        </p>
        <div class="mt-3 grid grid-cols-1 gap-2 text-xs text-gray-700">
          <span class="flex items-center gap-2">
            <span
              class="h-4 w-4 flex-shrink-0 rounded border border-gray-300"
              style="background-color: #98D87B;"
            />
            Billable
          </span>
          <span class="flex items-center gap-2">
            <span
              class="h-4 w-4 flex-shrink-0 rounded border border-gray-300"
              style="background-color: #7FD2E2;"
            />
            Client non-billable
          </span>
          <span class="flex items-center gap-2">
            <span
              class="h-4 w-4 flex-shrink-0 rounded border border-gray-300"
              style="background-color: #FFD362;"
            />
            Internal non-billable
          </span>
          <span class="flex items-center gap-2">
            <span class="h-4 w-4 flex-shrink-0 rounded border border-gray-300 bg-gray-100" />
            Non-working
          </span>
          <span class="flex items-center gap-2">
            <span
              class="h-4 w-4 flex-shrink-0 rounded border border-gray-300"
              style="background-color: #C4B5FD;"
            />
            PTO
          </span>
        </div>
      </div>
    </SideBar>

    <!-- Main Content -->
    <main class="employee-insights-work-area p-6">
      <!-- Global loading overlay -->
      <div
        v-if="isStaffInsightsLoading"
        class="absolute inset-0 z-30 flex items-center justify-center bg-gray-100/75"
      >
        <div class="flex w-full max-w-sm flex-col items-center rounded-lg border border-gray-200 bg-white px-12 py-10 shadow-lg">
          <div class="relative mb-2 p-3">
            <div class="w-16 h-16 rounded-full border-4 border-brand-blue border-t-transparent animate-spin" />
            <div class="absolute inset-0 flex items-center justify-center">
              <div class="w-8 h-8 rounded-full border-4 border-brand-green border-t-transparent animate-spin-reverse" />
            </div>
          </div>
          <div class="mt-4 text-sm font-semibold text-gray-800">
            Loading Staff Insights
          </div>
          <div class="mt-1 text-xs text-gray-500">
            {{ loadingProgressText }}
          </div>
          <div
            v-if="loadingProgress.totalWeeks"
            class="mt-4 h-2 w-[calc(100%-2rem)] overflow-hidden rounded-full bg-gray-200"
            role="progressbar"
            :aria-valuenow="loadingProgressPercent"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              class="h-full rounded-full bg-brand-blue transition-all duration-300"
              :style="{ width: `${loadingProgressPercent}%` }"
            />
          </div>
          <div
            v-if="loadingProgress.totalWeeks"
            class="mt-2 flex w-[calc(100%-2rem)] justify-between text-xs text-gray-500"
          >
            <span>{{ loadingProgress.completedWeeks }} / {{ loadingProgress.totalWeeks }} weeks</span>
            <span>{{ loadingProgressPercent }}%</span>
          </div>
          <button
            type="button"
            class="mb-2 mt-6 rounded border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-red-300 hover:text-red-600"
            @click="cancelLoading"
          >
            Cancel loading
          </button>
        </div>
      </div>

      <!-- Render either AllStaff or IndividualStaff based on route -->
      <router-view />
    </main>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import { useDsaReportsStore } from '@/pinia/dsaReportsStore';
import { useEmployeeInsightsStore } from '@/pinia/employeeInsightsStore';
import SideBar from '@/ReusableComponents/SideBar.vue';

const myStore = useDsaReportsStore();
const eiStore = useEmployeeInsightsStore();
const route = useRoute();

const isSummaryLoading = computed(() =>
  Object.values(eiStore.dailyStatsByStaffLoadingWeeks || {}).some(Boolean)
);

const isStaffInsightsLoading = computed(() =>
  eiStore.isActivitySummaryLoading || isSummaryLoading.value
);

const showHeatmapLegend = computed(() => route.name !== 'EmployeeDashboard');

const loadingSummaryText = computed(() => {
  const activeWeek = Object.entries(eiStore.dailyStatsByStaffLoadingWeeks || {})
    .find(([, loading]) => loading)?.[0];
  if (!activeWeek) return 'Loading summary';

  const [startEpoch, endEpoch] = activeWeek.split('-').map(Number);
  if (!Number.isFinite(startEpoch) || !Number.isFinite(endEpoch)) {
    return 'Loading summary';
  }

  return `Loading ${formatShortDate(startEpoch)} - ${formatShortDate(endEpoch)}`;
});

const loadingProgress = computed(() => eiStore.dailyStatsByStaffLoadProgress || {
  totalWeeks: 0,
  completedWeeks: 0,
  activeWeeks: 0,
  failedWeeks: 0,
  currentWeekLabel: ''
});

const loadingProgressPercent = computed(() => {
  if (!loadingProgress.value.totalWeeks) return 0;
  return Math.min(100, Math.round(
    (loadingProgress.value.completedWeeks / loadingProgress.value.totalWeeks) * 100
  ));
});

const loadingProgressText = computed(() => {
  if (!loadingProgress.value.totalWeeks) return loadingSummaryText.value;
  const activeText = loadingProgress.value.activeWeeks
    ? `${loadingProgress.value.activeWeeks} week${loadingProgress.value.activeWeeks === 1 ? '' : 's'} loading`
    : 'Finalizing';
  const failedText = loadingProgress.value.failedWeeks
    ? `, ${loadingProgress.value.failedWeeks} failed`
    : '';
  return `${activeText}${failedText}`;
});

function formatShortDate(epochSeconds) {
  return new Date(epochSeconds * 1000)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function cancelLoading() {
  eiStore.cancelEmployeeInsightsLoading();
}

onBeforeRouteLeave(() => {
  eiStore.cancelEmployeeInsightsLoading();
});

onMounted(() => {
  myStore.fetchUserData();
});
</script>

<style scoped>
@keyframes spin-reverse {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(-360deg); }
}

.animate-spin-reverse {
  animation: spin-reverse 1.5s linear infinite;
}

.employee-insights-layout {
  display: flex;
  height: 100%;
  min-height: 0;
  overflow: hidden;
}

.employee-insights-work-area {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}
</style>
